<template>
  <div class="task-list-container">
    <!-- 全屏加载覆盖 -->
    <div v-if="pageLoading" class="page-loading-overlay">
      <div class="loader-card">
        <div class="spinner"></div>
        <div class="loader-text">正在加载...</div>
      </div>
    </div>
    <!-- 顶部工具栏 -->
    <div class="toolbar-apple">
      <div class="toolbar-left">
        <h1 class="page-title">📋 任务管理</h1>
        <div class="task-count-badge">{{ total }} 个任务</div>
      </div>
      <div class="toolbar-right">
        <button class="btn-apple-success" @click="generateWeeklyPlan" :disabled="planLoading">
          <span class="btn-icon">{{ planLoading ? '⏳' : '🎯' }}</span>
          <span class="btn-text">生成周计划</span>
        </button>
        <button class="btn-apple-primary" @click="showAddDialog = true">
          <span class="btn-icon">➕</span>
          <span class="btn-text">添加任务</span>
        </button>
      </div>
    </div>

    <!-- 筛选标签 -->
    <div class="filter-section">
      <div class="filter-tabs">
        <button 
          v-for="filter in filters" 
          :key="filter.value"
          class="filter-tab"
          :class="{ active: currentFilter === filter.value }"
          @click="currentFilter = filter.value">
          <span class="filter-icon">{{ filter.icon }}</span>
          <span class="filter-label">{{ filter.label }}</span>
          <span class="filter-count">{{ getFilterCount(filter.value) }}</span>
        </button>
      </div>
    </div>

    <!-- 任务卡片网格 -->
    <div class="tasks-grid-apple">
      <div v-for="task in filteredTasks" :key="task.id" class="task-card-list">
        <!-- 任务状态指示器 -->
        <div class="task-status-bar" :class="'status-' + task.status"></div>
        
        <!-- 任务内容 -->
        <div class="task-content-list">
          <div class="task-header-list">
            <div class="task-title-list">{{ task.taskName }}</div>
            <div class="task-badges">
              <span class="badge-importance" :class="'importance-' + getImportanceLevel(task.importance)">
                {{ task.importance }}
              </span>
              <span class="badge-priority" :class="getPriorityClass(task.priorityScore)">
                {{ task.priorityScore }}
              </span>
            </div>
          </div>

          <div class="task-meta-list">
            <span class="meta-item-list">
              <span class="meta-icon">🏷️</span>
              {{ task.taskType }}
            </span>
            <span class="meta-item-list">
              <span class="meta-icon">⏱️</span>
              {{ task.requiredHours }}h
            </span>
            <span class="meta-item-list" :class="getDeadlineClass(task.deadline)">
              <span class="meta-icon">📅</span>
              {{ formatDeadline(task.deadline) }}
            </span>
            <span class="meta-item-list">
              <span class="meta-icon">📆</span>
              {{ task.scheduledDate ? formatScheduledDate(task.scheduledDate) : '未安排' }}
            </span>
          </div>

          <div class="task-actions-list">
            <button v-if="task.status === '待处理'" 
                    class="btn-task-action btn-start" 
                    @click="updateStatus(task, '进行中')">
              ▶️ 开始
            </button>
            <button v-if="task.status === '进行中'" 
                    class="btn-task-action btn-complete" 
                    @click="updateStatus(task, '已完成')">
              ✅ 完成
            </button>
            <button v-if="task.status === '已完成'" 
                    class="btn-task-action btn-done" 
                    disabled>
              ✓ 已完成
            </button>
            <button class="btn-task-action btn-edit" 
                    @click="openEditDialog(task)">
              ✏️ 修改
            </button>
            <button class="btn-task-action btn-delete" 
                    @click="deleteTask(task.id)">
              🗑️ 删除
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-apple">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        background
      />
    </div>

    <!-- 添加任务对话框 -->
    <div v-if="showAddDialog" class="modal-overlay" @click.self="showAddDialog = false">
      <div class="modal-apple">
        <div class="modal-header">
          <h2 class="modal-title">➕ 添加新任务</h2>
          <button class="modal-close" @click="showAddDialog = false">✕</button>
        </div>
        
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">任务名称</label>
            <input v-model="taskForm.taskName" class="form-input" placeholder="请输入任务名称" />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">任务类型</label>
              <select v-model="taskForm.taskType" class="form-select">
                <option value="">请选择</option>
                <option value="程序开发">程序开发</option>
                <option value="文章撰写">文章撰写</option>
                <option value="程序修改">程序修改</option>
                <option value="文章修改">文章修改</option>
                <option value="报告编写">报告编写</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label">重要程度</label>
              <select v-model="taskForm.importance" class="form-select">
                <option value="正常">正常</option>
                <option value="重要">重要</option>
                <option value="非常重要">非常重要</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">所需时间(小时)</label>
              <input v-model.number="taskForm.requiredHours" type="number" step="0.5" min="0.5" class="form-input" />
            </div>

            <div class="form-group">
              <label class="form-label">截止时间</label>
              <input v-model="taskForm.deadline" type="datetime-local" class="form-input" />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-modal-cancel" @click="showAddDialog = false">取消</button>
          <button class="btn-modal-confirm" @click="addTask" :disabled="loading">
            {{ loading ? '添加中...' : '确定' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 修改任务对话框 -->
    <div v-if="showEditDialog" class="modal-overlay" @click.self="showEditDialog = false">
      <div class="modal-apple">
        <div class="modal-header">
          <h2 class="modal-title">✏️ 修改任务</h2>
          <button class="modal-close" @click="showEditDialog = false">✕</button>
        </div>
        
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">任务名称</label>
            <input v-model="editForm.taskName" class="form-input" placeholder="请输入任务名称" />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">任务类型</label>
              <select v-model="editForm.taskType" class="form-select">
                <option value="程序开发">程序开发</option>
                <option value="文章撰写">文章撰写</option>
                <option value="程序修改">程序修改</option>
                <option value="文章修改">文章修改</option>
                <option value="报告编写">报告编写</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label">重要程度</label>
              <select v-model="editForm.importance" class="form-select">
                <option value="正常">正常</option>
                <option value="重要">重要</option>
                <option value="非常重要">非常重要</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">所需时间(小时)</label>
              <input v-model.number="editForm.requiredHours" type="number" step="0.5" min="0.5" class="form-input" />
            </div>

            <div class="form-group">
              <label class="form-label">截止时间</label>
              <input v-model="editForm.deadline" type="datetime-local" class="form-input" />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-modal-cancel" @click="showEditDialog = false">取消</button>
          <button class="btn-modal-confirm" @click="saveEdit" :disabled="editLoading">
            {{ editLoading ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as taskApi from '../api/task'

export default {
  name: 'TaskList',
  setup() {
    const tasks = ref([])
    const showAddDialog = ref(false)
    const showEditDialog = ref(false)
    const loading = ref(false)
    const editLoading = ref(false)
    const planLoading = ref(false)
    const pageLoading = ref(true)
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const currentFilter = ref('all')
    
    const taskForm = ref({
      taskName: '',
      taskType: '',
      importance: '正常',
      requiredHours: 1,
      deadline: ''
    })

    const editForm = ref({
      id: null,
      taskName: '',
      taskType: '',
      importance: '正常',
      requiredHours: 1,
      deadline: ''
    })

    const filters = [
      { value: 'all', label: '全部', icon: '📋' },
      { value: '待处理', label: '待处理', icon: '⏳' },
      { value: '进行中', label: '进行中', icon: '🔄' },
      { value: '已完成', label: '已完成', icon: '✅' }
    ]

    const filteredTasks = computed(() => {
      let filtered = []
      if (currentFilter.value === 'all') {
        filtered = [...tasks.value]
      } else {
        filtered = tasks.value.filter(t => t.status === currentFilter.value)
      }
      
      // 按创建时间倒序排列（最新的在前面），后端已排序，这里保持原顺序
      return filtered
    })

    const getFilterCount = (filterValue) => {
      if (filterValue === 'all') return tasks.value.length
      return tasks.value.filter(t => t.status === filterValue).length
    }

    const loadTasks = async () => {
      pageLoading.value = true
      try {
        const res = await taskApi.getTasksByPage(currentPage.value, pageSize.value)
        if (res.data.code === 200) {
          tasks.value = res.data.data.records
          total.value = res.data.data.total
        }
      } catch (error) {
        ElMessage.error('加载任务失败')
      } finally {
        pageLoading.value = false
      }
    }
    
    const handleSizeChange = (val) => {
      pageSize.value = val
      currentPage.value = 1
      loadTasks()
    }
    
    const handleCurrentChange = (val) => {
      currentPage.value = val
      loadTasks()
    }
    
    const generateWeeklyPlan = async () => {
      planLoading.value = true
      try {
        // 先分析任务负载
        const analysisRes = await taskApi.analyzeWorkload()
        if (analysisRes.data.code !== 200) {
          ElMessage.error('分析任务负载失败')
          planLoading.value = false
          return
        }
        
        const analysis = analysisRes.data.data
        let overtime = false
        
        // 如果需要加班，询问用户
        if (analysis.needOvertime) {
          const urgentList = analysis.urgentTaskNames && analysis.urgentTaskNames.length > 0 
            ? '\n\n⚡ 紧急任务：\n• ' + analysis.urgentTaskNames.join('\n• ')
            : ''
          
          let title = '任务负载预警'
          let messageHeader = ''
          
          if (analysis.isOverloaded) {
            messageHeader = '⚠️ 任务负载超出正常工作时间！'
          } else if (analysis.hasUrgentAndTight) {
            messageHeader = '⏰ 有紧急任务且今天剩余时间不足！'
          }
          
          const message = `${messageHeader}

📊 任务分析：
• 待完成总工时：${analysis.totalTaskHours.toFixed(1)} 小时
• 可用工作时间：${analysis.totalAvailableHours.toFixed(1)} 小时
• 今天剩余时间：${analysis.todayRemainingHours.toFixed(1)} 小时
• 紧急任务数：${analysis.urgentTaskCount} 个${urgentList}

${analysis.canFinishWithOvertime 
  ? '💡 如果每天加班2小时（22:00-24:00），可以更快完成任务。\n\n是否启用加班模式？' 
  : '⚠️ 即使加班也无法在截止日期前完成所有任务，建议调整任务优先级或延期部分任务。\n\n是否仍要启用加班模式？'}`
          
          try {
            await ElMessageBox.confirm(message, title, {
              confirmButtonText: '启用加班模式',
              cancelButtonText: '正常模式',
              type: 'warning',
              distinguishCancelAndClose: true
            })
            overtime = true
          } catch (action) {
            if (action === 'close') {
              planLoading.value = false
              return
            }
            // 用户选择正常模式
            overtime = false
          }
        }
        
        // 生成周计划
        const res = await taskApi.generateWeeklyPlan(overtime)
        if (res.data.code === 200) {
          ElMessage.success(overtime ? '加班模式周计划生成成功！' : '周计划生成成功！')
          ElMessageBox.alert(res.data.data, '周计划详情', {
            confirmButtonText: '确定',
            dangerouslyUseHTMLString: false
          })
          loadTasks()
        } else {
          ElMessage.error(res.data.message || '生成周计划失败')
        }
      } catch (error) {
        ElMessage.error('生成周计划失败：' + (error.message || '未知错误'))
      } finally {
        planLoading.value = false
      }
    }

    const addTask = async () => {
      if (!taskForm.value.taskName || !taskForm.value.taskType || !taskForm.value.deadline) {
        ElMessage.warning('请填写完整信息')
        return
      }
      
      loading.value = true
      try {
        // 转换日期格式
        const deadline = new Date(taskForm.value.deadline).toISOString().replace('T', ' ').substring(0, 19)
        const taskData = {
          ...taskForm.value,
          deadline
        }
        
        const res = await taskApi.addTask(taskData)
        if (res.data.code === 200) {
          ElMessage.success('添加成功')
          showAddDialog.value = false
          taskForm.value = { taskName: '', taskType: '', importance: '正常', requiredHours: 1, deadline: '' }
          loadTasks()
        } else {
          ElMessage.error(res.data.message)
        }
      } catch (error) {
        ElMessage.error('添加失败')
      } finally {
        loading.value = false
      }
    }

    const openEditDialog = (task) => {
      editForm.value.id = task.id
      editForm.value.taskName = task.taskName
      editForm.value.taskType = task.taskType
      editForm.value.importance = task.importance || '正常'
      editForm.value.requiredHours = task.requiredHours
      // 转换日期为 datetime-local 格式
      if (task.deadline) {
        const d = new Date(task.deadline)
        const pad = (n) => String(n).padStart(2, '0')
        editForm.value.deadline = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      } else {
        editForm.value.deadline = ''
      }
      showEditDialog.value = true
    }

    const saveEdit = async () => {
      if (!editForm.value.taskName || !editForm.value.taskType || !editForm.value.deadline) {
        ElMessage.warning('请填写完整信息')
        return
      }
      
      editLoading.value = true
      try {
        const deadline = new Date(editForm.value.deadline).toISOString().replace('T', ' ').substring(0, 19)
        const data = {
          taskName: editForm.value.taskName,
          taskType: editForm.value.taskType,
          importance: editForm.value.importance,
          requiredHours: editForm.value.requiredHours,
          deadline
        }
        
        const res = await taskApi.editTask(editForm.value.id, data)
        if (res.data.code === 200) {
          ElMessage.success('修改成功')
          showEditDialog.value = false
          loadTasks()
        } else {
          ElMessage.error(res.data.message || '修改失败')
        }
      } catch (error) {
        ElMessage.error('修改失败')
      } finally {
        editLoading.value = false
      }
    }

    const updateStatus = async (task, status) => {
      // 保存原状态，用于失败时回滚
      const originalStatus = task.status
      
      // 立即更新本地状态（乐观更新）
      task.status = status
      if (status === '已完成') {
        task.completedDate = new Date().toISOString()
      }
      
      try {
        const res = await taskApi.updateTaskStatus(task.id, status)
        if (res.data.code !== 200) {
          // 失败时回滚
          task.status = originalStatus
          task.completedDate = null
          ElMessage.error('更新失败')
        }
        // 成功时不显示消息，保持安静
      } catch (error) {
        // 失败时回滚
        task.status = originalStatus
        task.completedDate = null
        ElMessage.error('更新失败')
      }
    }

    const deleteTask = async (id) => {
      try {
        await ElMessageBox.confirm('确定删除该任务吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        // 先从本地列表移除（乐观更新）
        const index = tasks.value.findIndex(t => t.id === id)
        const removedTask = index >= 0 ? tasks.value.splice(index, 1)[0] : null
        total.value = Math.max(0, total.value - 1)
        
        const res = await taskApi.deleteTask(id)
        if (res.data.code !== 200) {
          // 失败时恢复
          if (removedTask && index >= 0) {
            tasks.value.splice(index, 0, removedTask)
            total.value += 1
          }
          ElMessage.error('删除失败')
        }
        // 成功时不显示消息
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }

    const formatDate = (date) => {
      if (!date) return ''
      return new Date(date).toLocaleString('zh-CN')
    }

    const formatDeadline = (date) => {
      if (!date) return ''
      const d = new Date(date)
      const now = new Date()
      const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24))
      
      if (diffDays < 0) return '已过期'
      if (diffDays === 0) return '今天'
      if (diffDays === 1) return '明天'
      return `${diffDays}天后`
    }

    const formatScheduledDate = (date) => {
      if (!date) return ''
      const d = new Date(date)
      return `${d.getMonth() + 1}/${d.getDate()}`
    }

    const getPriorityType = (score) => {
      if (score >= 80) return 'danger'
      if (score >= 60) return 'warning'
      return 'info'
    }

    const getPriorityClass = (score) => {
      if (score >= 80) return 'priority-high'
      if (score >= 60) return 'priority-medium'
      return 'priority-low'
    }

    const getStatusType = (status) => {
      const map = { '待处理': 'info', '进行中': 'warning', '已完成': 'success' }
      return map[status] || 'info'
    }

    const getImportanceType = (importance) => {
      const map = { '正常': '', '重要': 'warning', '非常重要': 'danger' }
      return map[importance] || ''
    }

    const getImportanceLevel = (importance) => {
      const map = { '正常': 'normal', '重要': 'important', '非常重要': 'critical' }
      return map[importance] || 'normal'
    }

    const getDeadlineClass = (deadline) => {
      const d = new Date(deadline)
      const now = new Date()
      const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24))
      
      if (diffDays < 0) return 'deadline-overdue'
      if (diffDays === 0) return 'deadline-today'
      if (diffDays === 1) return 'deadline-tomorrow'
      return 'deadline-normal'
    }

    onMounted(() => {
      loadTasks()
    })

    return {
      tasks,
      showAddDialog,
      showEditDialog,
      loading,
      editLoading,
      planLoading,
      currentPage,
      pageSize,
      total,
      currentFilter,
      filters,
      filteredTasks,
      taskForm,
      editForm,
      getFilterCount,
      addTask,
      openEditDialog,
      saveEdit,
      updateStatus,
      deleteTask,
      formatDate,
      formatDeadline,
      formatScheduledDate,
      getPriorityType,
      getPriorityClass,
      getStatusType,
      getImportanceType,
      getImportanceLevel,
      getDeadlineClass,
      handleSizeChange,
      handleCurrentChange,
      generateWeeklyPlan,
      pageLoading
    }
  }
}
</script>

<style scoped>
/* 全屏加载覆盖样式（苹果风格） */
.page-loading-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(242, 242, 247, 0.8);
  backdrop-filter: saturate(180%) blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}
.loader-card {
  background: white;
  border: 1px solid #e5e5ea;
  border-radius: 16px;
  padding: 16px 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  gap: 12px;
}
.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid #e5e5ea;
  border-top-color: #007AFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.loader-text { font-weight: 700; color: #1d1d1f; }
@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }

/* 容器 */
.task-list-container {
  position: relative;
  min-height: 100vh;
  background: #F2F2F7;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', sans-serif;
}

/* 工具栏 */
.toolbar-apple {
  background: white;
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  flex-wrap: wrap;
  gap: 16px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;
}

.task-count-badge {
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 122, 255, 0.2);
}

.toolbar-right {
  display: flex;
  gap: 12px;
}

.btn-apple-primary,
.btn-apple-success {
  padding: 12px 24px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-apple-primary {
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
}

.btn-apple-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 122, 255, 0.4);
}

.btn-apple-success {
  background: linear-gradient(135deg, #34C759 0%, #30D158 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(52, 199, 89, 0.3);
}

.btn-apple-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(52, 199, 89, 0.4);
}

.btn-apple-success:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* 筛选标签 */
.filter-section {
  background: white;
  border-radius: 20px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.filter-tabs {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-tab {
  padding: 12px 20px;
  border-radius: 12px;
  border: 2px solid #e5e5e7;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
}

.filter-tab:hover {
  border-color: #007AFF;
  background: #F5F5F7;
}

.filter-tab.active {
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  border-color: transparent;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 122, 255, 0.25);
}

.filter-count {
  background: rgba(0, 0, 0, 0.1);
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 12px;
}

.filter-tab.active .filter-count {
  background: rgba(255, 255, 255, 0.2);
}

/* 任务网格 */
.tasks-grid-apple {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.task-card-list {
  background: white;
  border-radius: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.task-card-list:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.task-status-bar {
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
}

.status-待处理 {
  background: #86868b;
}

.status-进行中 {
  background: linear-gradient(180deg, #FF9500 0%, #FF2D55 100%);
}

.status-已完成 {
  background: linear-gradient(180deg, #34C759 0%, #30D158 100%);
}

.task-content-list {
  padding-left: 12px;
}

.task-header-list {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.task-title-list {
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
  flex: 1;
  line-height: 1.4;
}

.task-badges {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.badge-importance,
.badge-priority {
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.importance-normal {
  background: #e8e8ed;
  color: #86868b;
}

.importance-important {
  background: #fff3cd;
  color: #856404;
}

.importance-critical {
  background: #f8d7da;
  color: #721c24;
}

.priority-high {
  background: #f8d7da;
  color: #721c24;
}

.priority-medium {
  background: #fff3cd;
  color: #856404;
}

.priority-low {
  background: #d1ecf1;
  color: #0c5460;
}

.task-meta-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.meta-item-list {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #86868b;
}

.deadline-overdue {
  color: #FF3B30;
  font-weight: 600;
}

.deadline-today {
  color: #FF2D55;
  font-weight: 600;
}

.deadline-tomorrow {
  color: #FF9500;
  font-weight: 600;
}

.task-actions-list {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-task-action {
  flex: 1;
  min-width: 80px;
  padding: 8px 12px;
  border-radius: 10px;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-start {
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  color: white;
}

.btn-complete {
  background: linear-gradient(135deg, #34C759 0%, #30D158 100%);
  color: white;
}

.btn-done {
  background: #e8e8ed;
  color: #86868b;
  cursor: not-allowed;
}

.btn-delete {
  background: #fff;
  color: #FF3B30;
  border: 2px solid #FF3B30;
}

.btn-edit {
  background: #fff;
  color: #FF9500;
  border: 2px solid #FF9500;
}

.btn-task-action:not(.btn-done):hover {
  transform: translateY(-2px);
}

/* 分页 */
.pagination-apple {
  background: white;
  border-radius: 20px;
  padding: 20px;
  display: flex;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow-x: auto;
}

.pagination-apple :deep(.el-pagination) {
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.pagination-apple :deep(.el-pagination__total),
.pagination-apple :deep(.el-pagination__sizes),
.pagination-apple :deep(.el-pagination__jump) {
  margin: 0;
  white-space: nowrap;
}

/* 模态框 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-apple {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid #e5e5e7;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;
}

.modal-close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f5f5f7;
  color: #86868b;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #e8e8ed;
  color: #1d1d1f;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 8px;
}

.form-input,
.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e5e7;
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.2s;
  font-family: inherit;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #007AFF;
  box-shadow: 0 0 0 4px rgba(0, 122, 255, 0.1);
}

.modal-footer {
  padding: 24px;
  border-top: 1px solid #e5e5e7;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-modal-cancel,
.btn-modal-confirm {
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-modal-cancel {
  background: #f5f5f7;
  color: #1d1d1f;
}

.btn-modal-cancel:hover {
  background: #e8e8ed;
}

.btn-modal-confirm {
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
}

.btn-modal-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 122, 255, 0.4);
}

.btn-modal-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* 响应式 */
@media (max-width: 768px) {
  .task-list-container {
    padding: 12px;
  }

  .toolbar-apple {
    padding: 16px;
  }

  .toolbar-left {
    width: 100%;
    flex-wrap: wrap;
  }

  .toolbar-right {
    width: 100%;
    justify-content: stretch;
  }

  .page-title {
    font-size: 20px;
  }

  .task-count-badge {
    font-size: 12px;
    padding: 6px 12px;
  }

  /* 按钮优化 - 保留文字 */
  .btn-apple-primary,
  .btn-apple-success {
    flex: 1;
    padding: 10px 16px;
    font-size: 13px;
    justify-content: center;
  }

  .btn-icon {
    font-size: 16px;
  }

  .tasks-grid-apple {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  /* 筛选标签优化 - 保留文字 */
  .filter-tabs {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .filter-tab {
    padding: 10px 12px;
    font-size: 13px;
    justify-content: center;
  }

  .filter-icon {
    font-size: 16px;
  }

  .filter-count {
    font-size: 11px;
    padding: 2px 6px;
  }

  /* 任务卡片优化 */
  .task-card-list {
    padding: 16px;
  }

  .task-title-list {
    font-size: 15px;
  }

  .task-meta-list {
    gap: 8px;
    font-size: 12px;
  }

  .meta-item-list {
    font-size: 12px;
  }

  .task-actions-list {
    gap: 6px;
  }

  .btn-task-action {
    min-width: 70px;
    padding: 8px 10px;
    font-size: 12px;
  }

  /* 分页优化 */
  .pagination-apple {
    padding: 12px;
  }

  .pagination-apple :deep(.el-pagination) {
    font-size: 12px;
  }

  .pagination-apple :deep(.el-pagination__sizes) {
    margin-right: 0;
  }

  .pagination-apple :deep(.el-pagination__jump) {
    margin-left: 0;
  }

  /* 模态框优化 */
  .modal-apple {
    margin: 12px;
  }

  .modal-header {
    padding: 20px;
  }

  .modal-title {
    font-size: 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-footer {
    padding: 20px;
  }
}

/* 超小屏幕优化 */
@media (max-width: 480px) {
  .page-title {
    font-size: 18px;
  }

  .toolbar-left {
    gap: 8px;
  }

  .task-count-badge {
    font-size: 11px;
    padding: 4px 10px;
  }

  .btn-apple-primary,
  .btn-apple-success {
    padding: 8px 12px;
    font-size: 12px;
  }

  .filter-tab {
    padding: 8px 10px;
    font-size: 12px;
  }

  .filter-icon {
    font-size: 14px;
  }

  .btn-text {
    font-size: 12px;
  }
}
</style>
